import React from 'react';
import {
  FormControl,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { InputProps } from '@material-ui/core/Input';
import { InputLabelProps } from '@material-ui/core/InputLabel';
import { Controller, useFormContext } from 'react-hook-form';
import { QuestionCircleIcon } from '../icon/solid';
import { useId } from '../../hooks';
import { ClassApi } from '../../types/styles';
import Tooltip from '../tooltip/Tooltip';
import useStyles from './styles';

type Props = Omit<InputProps, 'classes'> & {
  label?: string;
  tooltip?: string;
  name: string;
  hideErrors?: boolean;
  dontCheckEmptyValue?: boolean;
  dontCheckAfterSubmit?: boolean;
  type?: 'text' | 'password' | 'textarea' | 'number';
  classes?: ClassApi<typeof useStyles>;
  inputLabelProps?: Omit<InputLabelProps, 'classes'>;
  helpInfo?: string | React.ReactNode;
};

const ReactFormCheckbox = ({
  name,
  label,
  tooltip = '',
  disabled = false,
  hideErrors = false,
  dontCheckEmptyValue = true,
  dontCheckAfterSubmit = false,
  className,
  classes: externalClasses = {},
  helpInfo,
}: Props) => {
  const classes = useStyles({ classes: externalClasses });
  const { watch, formState: { isSubmitting, errors } } = useFormContext();
  const fieldValue = watch(name);
  const inputId = useId('form-input-');
  const errorId = useId('input-error-');
  const error = errors[name];
  const checkValue = dontCheckEmptyValue || fieldValue;
  const afterSubmit = dontCheckAfterSubmit && isSubmitting;

  return (
    <Controller
      name={name}
      render={({
        value, ref, onBlur, onChange,
      }) => (
        <FormControl
          id={inputId}
          fullWidth
          disabled={disabled}
          error={Boolean(!isSubmitting) && Boolean(!afterSubmit && checkValue && error)} // touched
          className={className}
          classes={{
            root: classes.root,
          }}
        >
          {label && (
            <Tooltip title={tooltip}>

              <FormControlLabel
                control={(
                  <Checkbox
                    onBlur={onBlur}
                    onChange={(e) => onChange(e.target.checked)}
                    checked={value}
                    inputRef={ref}
                    classes={{ root: classes.checkbox }}
                  />
                )}
                label={label}
                classes={{
                  label: classes.label,
                  root: classes.labelRoot,
                }}
              />
            </Tooltip>
          )}

          {!afterSubmit && checkValue && error && !hideErrors && ( // touched
            <FormHelperText id={errorId}>{error.message}</FormHelperText>
          )}
          {!(!afterSubmit && checkValue && !error && !hideErrors) && helpInfo && ( // touched
            <FormHelperText>
              <QuestionCircleIcon className={classes.helpIcon} />
              {helpInfo}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default ReactFormCheckbox;
